// Sovereign MUSE chat hook — WSS client, streaming, same interface shape as
// the commercial `useMuseChat` so the UI components don't care about the swap.
//
// Wire protocol: sovereign-gw WSS messages (init, response_start/chunk/end,
// thinking_*, tool_use, tool_result, agent_status, error, heartbeat).

import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import type { UIMessage } from 'ai';
import { getStoredToken } from './useAuth';
import type { ChatConfig, EffortLevel, ModelEntry } from '../shared/chat-config';
import { MAX_USER_MESSAGE_CHARS } from '../../shared/message-limits.js';
import { splitReasoning, REASONING_OPEN, REASONING_CLOSE } from '../utils/splitReasoning';
import { FALLBACK_COMPANION, type CompanionDescriptor } from '../brain/persona';

type ChatStatus = 'ready' | 'submitted' | 'streaming' | 'error';

export type TurnPhase =
  | { phase: 'submitted' }
  | { phase: 'thinking' }
  | { phase: 'tool_use'; toolName: string }
  | { phase: 'streaming' }
  | null;

export interface MessageMetrics {
  input_tokens: number;
  output_tokens: number;
  cache_read_input_tokens?: number;
  cache_creation_input_tokens?: number;
  total_cost_usd?: number;
  /** Concrete provider model reported after a rolling alias resolves. */
  resolved_model?: string;
  /** C4 message-footer stamp — the MUSE selection id running when this turn
   *  completed. Resolved against availableModels to render "<label> ·
   *  <substrate display name>" under the assistant message. */
  model?: string;
  duration_ms: number;
}

export interface WritingRoomDocumentUpdatedEvent {
  project_id: string;
  version: string;
  /** Present only when the write came from a companion's `apply_edit` MCP
   *  tool call (server-derived display name, e.g. "Rainer") — absent for an
   *  ordinary writer-initiated save/restore. */
  actor?: string;
}

// SYNC: RoomState must match gateway/types.ts RoomState exactly (WR-2,
// ADR-018) AND src/client/layers/writing-room/room-state.ts's own copy.
// Duplicated (not imported) so useChat.ts — a general-purpose hook — stays
// free of a dependency on the writing-room layer, mirroring how
// WritingRoomDocumentUpdatedEvent above is already a local duplicate of the
// gateway's wire shape rather than a cross-layer import.
export interface RoomState {
  project_id: string;
  version: string | null;
  status: 'loading' | 'saved' | 'saved_local' | 'dirty' | 'saving' | 'conflict' | 'error' | 'unbound';
  total_words: number;
  outline: Array<{ level: number; title: string; words: number; from: number; to: number }>;
  active: { from: number; to: number; title: string; words: number } | null;
  cursor: number | null;
  excerpt: { text: string; from: number; to: number; elided_before: number; elided_after: number } | null;
}

type AssistantPart =
  | { type: 'text'; text: string }
  | { type: 'reasoning'; text: string; state?: string }
  | { type: 'step-start' }
  | {
      type: string;
      toolCallId: string;
      toolName: string;
      state: 'input-streaming' | 'input-available' | 'output-available' | 'output-error';
      input?: unknown;
      output?: unknown;
      errorText?: string;
      textOffset?: number;
    }
  | { type: 'file'; mediaType: string; filename: string; url: string };

interface InFlightMessage {
  id: string;
  parts: AssistantPart[];
  textIndex: number | null;
  thinkingIndex: number | null;
  toolIndex: Map<string, number>;
  createdAt: Date;
  reasoningRaw: string;
}

interface SendMessageArgs {
  text: string;
  files?: Array<{ type: 'file'; mediaType: string; filename: string; url: string }>;
}

interface ActiveAgent {
  name: string;
  task: string;
  startedAt: number;
}

/** A provider safety-filter flag on the live turn (ADR-022). `count` is how many
 *  turns in a row were flagged since the last good answer. */
export interface SafeguardFlag {
  message: string;
  count: number;
}

/** ADR-023: a message typed while the companion was replying, not yet read. */
export interface QueuedInfo {
  /** True only while the message is still held server-side (Codex/Hermes/attachments excluded);
   *  on the Claude lane it is already in the CLI's hands and cannot be taken back. */
  takeBack: boolean;
}

/** What the chat surface needs to draw and manage waiting messages (ADR-023). */
export interface Interjections {
  queued: Map<string, QueuedInfo>;
  onTakeBack: (messageId: string) => void;
  takeBackDraft: { key: string; text: string } | null;
}

export interface UseMuseChatResult {
  /** Messages waiting to be read (ADR-023), keyed by message id. Their bubbles render dimmed. */
  queuedMessages: Map<string, QueuedInfo>;
  /** Take back a waiting message: the server cancels it and the text returns via takeBackDraft. */
  cancelQueued: (messageId: string) => void;
  /** Text handed back by a successful take-back; the composer inserts it once per distinct key. */
  takeBackDraft: { key: string; text: string } | null;
  /** Set while the active chat's latest turn was flagged and not yet recovered. */
  safeguardFlag: SafeguardFlag | null;
  messages: UIMessage[];
  messageMetrics: Map<string, MessageMetrics>;
  /** Common Room provenance (slice 1): message_id -> origin ('hermes_wake' |
   *  'reminder'). Populated from response_start on both live arrival and
   *  replay (unlike messageMetrics, never skipped during replay — provenance
   *  must survive reload). Absent entries render no chip. */
  messageOrigin: Map<string, string>;
  /** Session-bound wakes (slice 1): message_id -> resolved origin session
   *  label. Populated from response_start's origin_session_label field,
   *  which the server sends only when the row carries an origin_session_id
   *  (replay only today — see server.ts's WS replay path). A map entry with
   *  value null means "that session has no label (or is gone)"; a missing
   *  entry means "this message has no origin_session_id at all". */
  messageOriginSessionLabel: Map<string, string | null>;
  status: ChatStatus;
  isLoading: boolean;
  connected: boolean;
  switching: boolean;
  hasConnected: boolean;
  error: Error | null;
  activeAgents: ActiveAgent[];
  /** The model ID reported by the gateway in the most recent `init` message.
   *  null until the first successful connection. Use to sync client config with
   *  the server's live model on reconnect / page load. */
  serverModel: string | null;
  /** The model registry sent by the gateway in the most recent `init` message.
   *  null until the first successful connection. useChatConfig uses this as the
   *  live picker list; AVAILABLE_MODELS in chat-config.ts is the fallback seed. */
  serverModels: ModelEntry[] | null;
  /** The companion allowlist + display metadata sent by the gateway in the
   *  most recent `init` message. null until the first successful connection.
   *  NewSessionDialog/SettingsPanel/BrainStrip render from this; persona.ts's
   *  FALLBACK_COMPANION is the pre-init / not-found fallback. */
  serverCompanions: CompanionDescriptor[] | null;
  /** True while the server is replaying historical messages on connect/reconnect.
   *  MessageList suppresses per-message fade-up animations and defers the
   *  scroll-to-bottom until this flips false. */
  isReplaying: boolean;
  sendMessage: (args: SendMessageArgs) => void;
  stop: () => void;
  clearHistory: () => void;
  sendTTSRequest: (messageId: string, text: string, voiceId: string, requestId: string) => void;
  sendTTSStop: () => void;
  sendVoiceConfig: (autoSpeak: boolean, ttsMode: string, voiceId: string, speed: number) => void;
  sendReasoningConfig: (enabled: boolean, readReasoningAloud?: boolean) => void;
  showReasoning: boolean;
  /** The server-persisted read-reasoning-aloud toggle, seeded from the init frame. */
  readReasoningAloud: boolean;
  /** Ref to a callback invoked when the server sends show_reasoning in the init frame.
   *  App.tsx wires this to suppress echo when syncing the reasoning toggle. */
  onServerReasoningConfigRef: MutableRefObject<((enabled: boolean, readReasoningAloud: boolean) => void) | null>;
  /** Send an arbitrary JSON message over the live WS connection.
   *  Used by useReactions to send reaction frames without coupling to internal wsRef. */
  sendRawWs: (payload: Record<string, unknown>) => void;
  /** Shared-socket seam for Writing Room document update fan-out. */
  onWritingRoomDocumentUpdatedRef: MutableRefObject<((event: WritingRoomDocumentUpdatedEvent) => void) | null>;
  /** WR-2 (ADR-018) getter-pattern seam: WritingRoomLayer installs a GETTER
   *  here (not a value) once bound and not loading — nothing runs on the hot
   *  typing path. sendMessage calls roomStateRef.current?.() once per send
   *  and attaches the result to the outgoing 'message' frame. null/unset
   *  when not mounted in a bound, ready Writing Room — the frame is then
   *  byte-identical to pre-WR-2 (no room_state key). */
  roomStateRef: MutableRefObject<(() => RoomState | null) | null>;
  onServerAudioChunkRef: MutableRefObject<((data: ArrayBuffer) => void) | null>;
  onTTSStartRef: MutableRefObject<((messageId: string, sampleRate: number, voiceId: string, sourceId?: string) => void) | null>;
  onTTSEndRef: MutableRefObject<((messageId: string, sourceId?: string) => void) | null>;
  onTTSErrorRef: MutableRefObject<((messageId: string, sourceId?: string) => void) | null>;
  sttAvailable: boolean;
  sttStreamingAvailable: boolean;
  sendSTTAudio: (blob: Blob, format?: string) => void;
  sendSTTStreamStart: (language?: string) => void;
  sendSTTStreamStop: () => void;
  sendBinaryFrame: (data: ArrayBuffer) => void;
  onSTTResultRef: MutableRefObject<((text: string) => void) | null>;
  onSTTStreamSegmentRef: MutableRefObject<((text: string, completed: boolean) => void) | null>;
  onSTTStreamReadyRef: MutableRefObject<(() => void) | null>;
  onSTTStreamDoneRef: MutableRefObject<(() => void) | null>;
  onSTTStreamDisconnectRef: MutableRefObject<(() => void) | null>;
  onSTTStreamErrorRef: MutableRefObject<((message: string, fallback: boolean) => void) | null>;
  /** Ref to a callback invoked when the gateway sends a reaction frame.
   *  ChatArea wires this to useReactions.mergeServerReaction. */
  onReactionRef: MutableRefObject<((messageId: string, emoji: string, user: string, action: 'add' | 'remove') => void) | null>;
  /** Ref to a callback invoked when the gateway sends a reaction_sync frame.
   *  ChatArea wires this to useReactions.loadReactions. */
  onReactionSyncRef: MutableRefObject<((reactions: Array<{ message_id: string; emoji: string; user: string }>) => void) | null>;
  /** Reactions that arrived before onReactionRef was wired. ChatArea flushes these
   *  inside the useEffect that wires onReactionRef. */
  pendingReactionsRef: MutableRefObject<Array<{ messageId: string; emoji: string; user: string; action: 'add' | 'remove' }>>;
  /** reaction_sync payloads that arrived before onReactionSyncRef was wired. */
  pendingReactionSyncsRef: MutableRefObject<Array<Array<{ message_id: string; emoji: string; user: string }>>>;
  /** Current phase of an in-flight turn. null when no turn is active. */
  turnPhase: TurnPhase;
  /** Timestamp (Date.now()) when the current turn started. null when no turn is active. */
  turnStartedAt: number | null;
  /** Whether the subprocess reported itself alive in the last heartbeat. */
  subprocessAlive: boolean;
  /** Non-null while a client-originated model switch awaits user confirmation.
   *  The dialog shows `from` → `to` and calls confirmModelSwitch / cancelModelSwitch. */
  pendingModelSwitch: { from: string; to: string; effort: EffortLevel; session_id: string } | null;
  /** Confirms the pending model switch and sends config_change to the gateway. */
  confirmModelSwitch: () => void;
  /** Cancels the pending model switch and reverts chatConfig.model to the last-sent model. */
  cancelModelSwitch: () => void;
}

export interface ServerVoiceConfig {
  autoSpeak: boolean;
  ttsMode: 'browser' | 'server';
  voiceId: string;
  speed: number;
}

export function useMuseChat(
  chatConfig?: ChatConfig,
  sessionId?: string | null,
  onServerVoiceConfig?: (vc: ServerVoiceConfig) => void,
  /** Called synchronously in the init handler to sync chatConfig.model in the
   *  same React batch as setServerModel. Eliminates the render-cycle gap where
   *  the config_change self-seed fires before chatConfig.model is updated to the
   *  server's authoritative value (Option A fix). */
  onSyncChatModel?: (model: string, serverModels: ModelEntry[]) => void,
  /** Called by cancelModelSwitch to revert chatConfig.model to the last-sent model. */
  onRevertChatModel?: (model: string) => void,
  /** PATH B SLICE 4 — the ACTIVE session's companion identity (e.g. 'rook' |
   *  'rainer'), sourced from the server-authoritative session row. Connects to
   *  /ws/${sessionIdentity}. FALLBACK_COMPANION is allowed only when there is
   *  genuinely no explicit session target (`sessionId === undefined`), which
   *  preserves the legacy single-companion OSS entry path. An explicit session
   *  id without an identity fails closed: no socket is constructed. */
  sessionIdentity?: string | null,
): UseMuseChatResult {
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [messageMetrics, setMessageMetrics] = useState<Map<string, MessageMetrics>>(new Map());
  const [messageOrigin, setMessageOrigin] = useState<Map<string, string>>(new Map());
  const [messageOriginSessionLabel, setMessageOriginSessionLabel] = useState<Map<string, string | null>>(new Map());
  const [status, setStatus] = useState<ChatStatus>('ready');
  // Read at send time (not a dep) to tell a new turn from an interjection.
  const statusRef = useRef<ChatStatus>('ready');
  statusRef.current = status;
  const [queuedMessages, setQueuedMessages] = useState<Map<string, QueuedInfo>>(new Map());
  const [takeBackDraft, setTakeBackDraft] = useState<{ key: string; text: string } | null>(null);
  const [connected, setConnected] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // ADR-022. Keyed by session so a flag can never show on another chat.
  const [safeguardState, setSafeguardState] = useState<{ sessionId: string | null | undefined; flag: SafeguardFlag } | null>(null);
  const [activeAgents, setActiveAgents] = useState<ActiveAgent[]>([]);
  // The model ID reported by the gateway in the most recent init message.
  // Used to sync useChatConfig on page load / reconnect.
  const [serverModel, setServerModel] = useState<string | null>(null);
  // The model registry sent by the gateway in the init frame.
  // useChatConfig renders the picker from this list; AVAILABLE_MODELS is the fallback seed.
  const [serverModels, setServerModels] = useState<ModelEntry[] | null>(null);
  // The companion allowlist + display metadata sent by the gateway in the init frame.
  const [serverCompanions, setServerCompanions] = useState<CompanionDescriptor[] | null>(null);

  // A missing identity is safe only on the historical no-target OSS path. Once
  // a concrete session id exists, its server row is the sole authority for the
  // companion. Falling back there could turn an unresolved Rainer session into
  // a live /ws/rook connection.
  const connectionIdentity = sessionIdentity
    ?? (sessionId === undefined ? FALLBACK_COMPANION.id : null);

  // Stable ref mirror — reconnect timers and visibility recovery read this at
  // call time. A null value is a hard no-connect gate inside connect() itself,
  // not merely in the mount effect, so every reconnect path fails closed too.
  const identityRef = useRef<string | null>(connectionIdentity);
  identityRef.current = connectionIdentity;

  const [turnPhase, setTurnPhase] = useState<TurnPhase>(null);
  const turnStartedAtRef = useRef<number | null>(null);
  const [subprocessAlive, setSubprocessAlive] = useState(true);

  const wsRef = useRef<WebSocket | null>(null);
  const inFlightRef = useRef<InFlightMessage | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  // Tracks the highest seq seen from the server. Sent as last_seen_seq on
  // reconnect so the server only replays messages the client hasn't seen yet.
  // ADR 2026-06-12: never seeded from storage on mount — mount is always holding=0.
  const maxSeqRef = useRef(0);
  // Stable ref mirror of messages state — read in connect() to compute holding
  // without a stale closure. Synced via useEffect below.
  const messagesRef = useRef<UIMessage[]>([]);
  const reconnectDelayRef = useRef<number>(1000);
  const hasConnectedRef = useRef(false);
  const [startupGrace, setStartupGrace] = useState(true);
  // Track which session the current WS connection belongs to
  const connectedSessionRef = useRef<string | null | undefined>(undefined);
  // Track the last model+effort sent to the gateway so we only send on actual changes.
  // Seeded from the init message to avoid spurious config_change on first connect.
  const lastSentConfigRef = useRef<{ model: string; effort: EffortLevel } | null>(null);
  // Thorn diagnosis (2026-08): the config_change gate effect used to bare-return
  // when wsRef.current wasn't OPEN at the moment chatConfig.model changed — the
  // user's model-switch intent was silently discarded (no pendingModelSwitch, no
  // dialog, no log). queuedModelSwitchRef holds that intent across the gap; it's
  // resolved once the next init frame's onSyncChatModel settles (see the init
  // handler and the gate effect below).
  const queuedModelSwitchRef = useRef<{ model: string; effort: EffortLevel; requestedAt: number } | null>(null);
  // Baseline chatConfig seen by the gate effect while the socket was NOT OPEN —
  // lets the effect tell "user changed the picker while offline" (queue it) apart
  // from "this is just the effect's first mount-time firing" (no real intent yet,
  // don't queue a phantom switch against whatever the server eventually syncs).
  const offlineConfigBaselineRef = useRef<{ model: string; effort: EffortLevel } | null>(null);
  // Ref mirror for switching — sendMessage callback reads this without needing
  // to be re-created on every switching state change.
  const switchingRef = useRef(false);
  // Safety timeout ref: resets switching state if the server never sends ready/error
  const switchingTimeoutRef = useRef<number | null>(null);
  // Consecutive WS failures without a successful open — used to detect 401 upgrade
  // rejections which arrive as close code 1006 (not 1008/4401).
  const consecutiveWsFailuresRef = useRef(0);
  // Debounce timer for visibility beacon — avoids flooding on rapid tab-switches.
  const visibilityBeaconTimerRef = useRef<number | null>(null);

  // Replay window tracking — suppresses message fade-up animation and defers
  // scroll-to-bottom until the server has finished replaying history.
  // Counter is the primary signal (counts down from transcript_length).
  // Debounce timer is the backstop in case a frame is missed or miscounted.
  const [isReplaying, setIsReplaying] = useState(true);
  const isReplayingRef = useRef(true);
  const replayCounterRef = useRef(0);
  const replayDebounceRef = useRef<number | null>(null);
  // Accumulates fully-assembled UIMessages during replay. Flushed as ONE
  // setMessages call in settleReplay() — collapses O(n²) render flood to O(1).
  const replayBufferRef = useRef<UIMessage[]>([]);

  // TTS callback refs — wired by useVoice after hook instantiation.
  // Using refs (not props) so useChat doesn't need to re-create callbacks when
  // the handlers change. useVoice writes to these refs once on mount.
  const onServerAudioChunkRef = useRef<((data: ArrayBuffer) => void) | null>(null);
  const onTTSStartRef = useRef<((messageId: string, sampleRate: number, voiceId: string, sourceId?: string) => void) | null>(null);
  const onTTSEndRef = useRef<((messageId: string, sourceId?: string) => void) | null>(null);
  // Reeve HIGH, PR #68 2nd round — tts_notice's failure case needs an
  // id-scoped signal to reach useVoice (mirrors onTTSEndRef's own shape
  // exactly), not just a console.warn: the server engine's failed-
  // generation path already sends tts_end (draining the client to an
  // ordinary "finished successfully" state) immediately before this
  // notice, so without THIS callback a consumer watching speakingId alone
  // (the Writing Room's chapter queue) can never tell that drain apart
  // from a real finish.
  const onTTSErrorRef = useRef<((messageId: string, sourceId?: string) => void) | null>(null);

  // STT state and refs
  const [sttAvailable, setSttAvailable] = useState(false);
  const [sttStreamingAvailable, setSttStreamingAvailable] = useState(false);
  const onSTTResultRef = useRef<((text: string) => void) | null>(null);
  const onSTTStreamSegmentRef = useRef<((text: string, completed: boolean) => void) | null>(null);
  const onSTTStreamReadyRef = useRef<(() => void) | null>(null);
  const onSTTStreamDoneRef = useRef<(() => void) | null>(null);
  const onSTTStreamDisconnectRef = useRef<(() => void) | null>(null);
  const onSTTStreamErrorRef = useRef<((message: string, fallback: boolean) => void) | null>(null);

  // Reaction callback refs — wired by ChatArea after instantiation.
  const onReactionRef = useRef<((messageId: string, emoji: string, user: string, action: 'add' | 'remove') => void) | null>(null);
  const onReactionSyncRef = useRef<((reactions: Array<{ message_id: string; emoji: string; user: string }>) => void) | null>(null);
  // Reactions that arrive before the ref is wired (race window on first render).
  // ChatArea flushes these in the same useEffect that wires onReactionRef.
  const pendingReactionsRef = useRef<Array<{ messageId: string; emoji: string; user: string; action: 'add' | 'remove' }>>([]);
  const pendingReactionSyncsRef = useRef<Array<Array<{ message_id: string; emoji: string; user: string }>>>([]);
  const onWritingRoomDocumentUpdatedRef = useRef<((event: WritingRoomDocumentUpdatedEvent) => void) | null>(null);
  // WR-2 (ADR-018) — see UseMuseChatResult.roomStateRef doc comment.
  const roomStateRef = useRef<(() => RoomState | null) | null>(null);

  // Stable ref for the onServerVoiceConfig callback — avoids re-creating
  // handleServerMessage when the parent passes a new function reference.
  const onServerVoiceConfigRef = useRef(onServerVoiceConfig);
  onServerVoiceConfigRef.current = onServerVoiceConfig;
  // Stable refs for the new model-sync callbacks (same pattern as above).
  const onSyncChatModelRef = useRef(onSyncChatModel);
  onSyncChatModelRef.current = onSyncChatModel;
  const onRevertChatModelRef = useRef(onRevertChatModel);
  onRevertChatModelRef.current = onRevertChatModel;

  // Reasoning toggle state — seeded from the init frame's show_reasoning.
  const [showReasoning, setShowReasoning] = useState(true);
  // Read-reasoning-aloud toggle state — seeded from the init frame's read_reasoning_aloud.
  const [readReasoningAloud, setReadReasoningAloud] = useState(false);
  // Model switch confirmation gate — non-null when a client-originated model
  // change is pending user approval. Cleared on confirm, cancel, or WS close.
  const [pendingModelSwitch, setPendingModelSwitch] = useState<{
    from: string;
    to: string;
    effort: EffortLevel;
    session_id: string;
  } | null>(null);
  // Ref mirror so confirm/cancel callbacks don't need pendingModelSwitch in deps.
  const pendingModelSwitchRef = useRef(pendingModelSwitch);
  pendingModelSwitchRef.current = pendingModelSwitch;
  // Render-current session target. Confirmation uses this in addition to the
  // socket-bound session so pending intent can never cross a session transition.
  const activeSessionIdRef = useRef(sessionId);
  activeSessionIdRef.current = sessionId;
  // Stable ref for the onServerReasoningConfig callback — wired by App.tsx.
  const onServerReasoningConfigRef = useRef<((enabled: boolean, readReasoningAloud: boolean) => void) | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setStartupGrace(false), 2000);
    return () => clearTimeout(t);
  }, []);

  // Keep messagesRef in sync with messages state. Used by connect() to compute
  // holding without a stale closure.
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Snapshot-based state update. CRITICAL: we capture `inFlight.id` and
  // `inFlight.parts` OUTSIDE the setMessages updater. The updater function
  // may run multiple times under React StrictMode/concurrent rendering; if
  // we read from inFlightRef inside, we'd see mutated/cleared state on the
  // second run and crash with `Cannot read properties of null (reading 'id')`.
  const commitInFlight = useCallback(() => {
    const inFlight = inFlightRef.current;
    if (!inFlight) return;
    const snapshotId = inFlight.id;
    const snapshotParts = [...inFlight.parts];
    // Use the server-provided timestamp from response_start when available so
    // replayed assistant messages carry historically accurate times (not replay-time=now).
    // Falls back to new Date() only for live messages where no timestamp was sent.
    const snapshotTime = inFlight.createdAt instanceof Date ? inFlight.createdAt : new Date(inFlight.createdAt ?? Date.now());
    // Clear inFlightRef BEFORE the replay branch: snapshot already taken above;
    // clearing here avoids a null read if a concurrent render fires between the
    // replay push and the return statement below.
    inFlightRef.current = null;

    if (isReplayingRef.current) {
      // During replay: push the completed message into the buffer instead of
      // calling setMessages — avoids O(n²) render flood on long transcripts.
      const snapshot: UIMessage = {
        id: snapshotId,
        role: 'assistant',
        parts: snapshotParts as UIMessage['parts'],
        createdAt: snapshotTime,
      } as UIMessage;
      const buf = replayBufferRef.current;
      const existingIdx = buf.findIndex((m) => m.id === snapshotId);
      if (existingIdx >= 0) {
        buf[existingIdx] = snapshot;
      } else {
        buf.push(snapshot);
      }
      return;
    }

    setMessages((prev) => {
      const existingIdx = prev.findIndex((m) => m.id === snapshotId);
      const snapshot: UIMessage = {
        id: snapshotId,
        role: 'assistant',
        parts: snapshotParts as UIMessage['parts'],
        createdAt: snapshotTime,
      } as UIMessage;
      if (existingIdx >= 0) {
        const copy = [...prev];
        copy[existingIdx] = snapshot;
        return copy;
      }
      return [...prev, snapshot];
    });
  }, []);

  const updateInFlight = useCallback((mutator: (m: InFlightMessage) => void) => {
    const inFlight = inFlightRef.current;
    if (!inFlight) return;
    mutator(inFlight);

    // During replay: skip the per-chunk setMessages call entirely.
    // The in-flight accumulator is still being built in memory; commitInFlight
    // will push the finished message to replayBufferRef when response_end fires.
    if (isReplayingRef.current) return;

    const snapshotId = inFlight.id;
    const snapshotParts = [...inFlight.parts];
    const snapshotTime = inFlight.createdAt;
    setMessages((prev) => {
      const existingIdx = prev.findIndex((m) => m.id === snapshotId);
      const snapshot: UIMessage = {
        id: snapshotId,
        role: 'assistant',
        parts: snapshotParts as UIMessage['parts'],
        createdAt: snapshotTime,
      } as UIMessage;
      if (existingIdx >= 0) {
        const copy = [...prev];
        copy[existingIdx] = snapshot;
        return copy;
      }
      return [...prev, snapshot];
    });
  }, []);

  // Clears the replay window: cancels the debounce timer, flushes the replay
  // buffer in a SINGLE setMessages call, then flips isReplaying false.
  // Called by the counter (hits 0) and by the debounce backstop (no new frame
  // for 250ms). Both paths are needed: counter alone can miss if the server omits
  // a frame; debounce alone would delay settle on a fast connection.
  const settleReplay = useCallback(() => {
    if (replayDebounceRef.current !== null) {
      clearTimeout(replayDebounceRef.current);
      replayDebounceRef.current = null;
    }
    if (isReplayingRef.current) {
      isReplayingRef.current = false;

      // Flush buffered replay messages in one paint.
      // Snapshot and clear the buffer BEFORE calling setMessages so that any
      // concurrent render sees a stable (empty) buffer, not a live one.
      const buffered = replayBufferRef.current;
      replayBufferRef.current = [];

      if (buffered.length > 0) {
        setMessages((prev) => {
          // Merge: buffered messages may overlap with messages already in
          // state (prev should be empty at settle, but upsert by id to be safe).
          // The gateway replays oldest→newest by seq, so the buffer's INSERTION
          // ORDER is already correct — do NOT sort by createdAt here (assistant
          // messages are stamped at commit time / now, which would put ALL
          // assistant messages after ALL user messages and scramble the conversation).
          const merged = [...prev];
          for (const msg of buffered) {
            const idx = merged.findIndex((m) => m.id === msg.id);
            if (idx >= 0) {
              merged[idx] = msg;
            } else {
              merged.push(msg);
            }
          }
          return merged;
        });
      }

      setIsReplaying(false);
    }
  }, []);

  // Resets the 250ms debounce timer. Called on every replay frame so the
  // backstop only fires if no new frame arrives for 250ms.
  const armReplayDebounce = useCallback(() => {
    if (replayDebounceRef.current !== null) clearTimeout(replayDebounceRef.current);
    replayDebounceRef.current = window.setTimeout(settleReplay, 250);
  }, [settleReplay]);

  // Decrements the replay counter. Settles immediately when it reaches 0.
  const tickReplay = useCallback(() => {
    replayCounterRef.current -= 1;
    armReplayDebounce();
    if (replayCounterRef.current <= 0) settleReplay();
  }, [armReplayDebounce, settleReplay]);

  // ADR-023: a waiting message the local list has never seen (typed on another
  // device, or before a reload) still needs its bubble.
  const addUserBubbleIfMissing = useCallback((id: string, content: string, createdAt?: string) => {
    const bubble = {
      id,
      role: 'user',
      parts: [{ type: 'text', text: content }],
      createdAt: createdAt ? new Date(createdAt) : new Date(),
    } as unknown as UIMessage;
    if (isReplayingRef.current) {
      const buf = replayBufferRef.current;
      if (!buf.some((m) => m.id === id)) buf.push(bubble);
      return;
    }
    setMessages((prev) => (prev.some((m) => m.id === id) ? prev : [...prev, bubble]));
  }, []);

  const handleServerMessage = useCallback((msg: Record<string, unknown>) => {
    const type = msg.type as string;
    switch (type) {
      case 'init': {
        // Arm the replay counter from the server's transcript length.
        // Each replay_user_message and each response_end{finish_reason:'replay'}
        // ticks the counter down. When it hits 0 (or the 250ms debounce fires),
        // isReplaying flips false and MessageList does its one-shot scroll snap.
        const transcriptLen = typeof msg.transcript_length === 'number' ? msg.transcript_length : 0;
        // transcript_length counts stored messages (user + assistant pairs = 2 each).
        // Server fires one replay_user_message per user turn and one response_end
        // per assistant turn, so the expected frame count equals transcript_length.
        replayCounterRef.current = transcriptLen;
        // Always clear the replay buffer on init — a reconnect mid-replay would
        // otherwise leave stale partial data that bleeds into the new session's
        // flush (B5 fix). All three branches below may reach settleReplay(); the
        // buffer must be empty before any of them run.
        replayBufferRef.current = [];
        if (transcriptLen === 0) {
          // Empty history — settle immediately so we never get stuck.
          settleReplay();
        } else {
          // Server will stream replay frames. Arm the replay window and let the
          // counter/debounce settle it deterministically.
          //
          // ADR 2026-06-12: removed the maxSeqRef.current >= transcriptLen branch
          // that compared SEQ against COUNT — a latent mis-settle that would silently
          // fire if any row is ever deleted. The holding handshake now owns the
          // full-vs-gap replay decision; the client declares state, the server decides
          // behavior. No SEQ-vs-COUNT comparison survives.
          //
          // Same-page reconnect (holding=1): server gap-replays only new frames.
          // If client was at tip, zero frames arrive and debounce fires after 250ms.
          // No blink: existing messages stay in state throughout.
          //
          // Cold mount (holding=0): server full-replays all history. Counter ticks
          // to 0 and settles, or debounce fires first.
          isReplayingRef.current = true;
          setIsReplaying(true);
          armReplayDebounce();
        }

        if (msg.model && msg.effort) {
          // Thorn diagnosis: onSyncChatModel below overwrites chatConfig.model with
          // server truth, erasing an unsent local pick with zero trace. If there's a
          // live pending dialog or a queued offline intent that disagrees with the
          // incoming server model, log the conflict before it's overwritten — the gate
          // effect resolves the queued case below; the live-dialog case just loses its
          // "from" anchor (dialog itself is untouched, still resolved by confirm/cancel).
          const unresolvedIntent = pendingModelSwitchRef.current?.to ?? queuedModelSwitchRef.current?.model;
          if (unresolvedIntent && unresolvedIntent !== msg.model) {
            console.warn('[model-switch] sync overwrote model with unresolved user intent', {
              intent: unresolvedIntent,
              synced: msg.model,
              source: pendingModelSwitchRef.current ? 'pending' : 'queued',
            });
          }
          // Expose the live model to the parent (drives the chat-bar display).
          setServerModel(msg.model as string);
          // Sync chatConfig.model in the SAME React batch as setServerModel.
          // Passing msg.models lets setModel resolve the entry even before
          // liveModels populates (which happens one render cycle later via
          // the App.tsx setLiveModels effect). This eliminates the race where
          // the config_change effect's self-seed fires with chatConfig.model
          // still at the stale client default rather than the server model.
          onSyncChatModelRef.current?.(
            msg.model as string,
            Array.isArray(msg.models) ? (msg.models as ModelEntry[]) : [],
          );
          // Reset lastSentConfigRef so the config_change self-seed fires with
          // the correct chatConfig.model (being set synchronously above).
          lastSentConfigRef.current = null;
        }
        // Update the live model registry from the gateway. App.tsx mirrors this
        // into useChatConfig.setLiveModels so the picker always shows the live list.
        // ModelEntry (chat-config.ts) mirrors the wire ClientSafeModel shape exactly;
        // supportsEffort is required on both sides so no provider-based fallback needed.
        if (Array.isArray(msg.models)) {
          setServerModels(msg.models as ModelEntry[]); // wire shape matches ModelEntry
        }
        // PATH B SLICE 4: companion allowlist + display metadata, wire shape
        // matches CompanionDescriptor exactly (server.ts's listCompanions()).
        if (Array.isArray(msg.companions)) {
          setServerCompanions(msg.companions as CompanionDescriptor[]);
        }
        if (typeof msg.stt_available === 'boolean') {
          setSttAvailable(msg.stt_available as boolean);
        }
        if (typeof msg.stt_streaming_available === 'boolean') {
          setSttStreamingAvailable(msg.stt_streaming_available as boolean);
        }
        // Diagnostic — visible in DevTools AND sent to gateway for server-side logging.
        // Fires on every init (including reconnects) so we can diagnose STT tier issues
        // on mobile without needing DevTools access.
        const sttDiag = {
          src: 'init', stt: msg.stt_available, streaming: msg.stt_streaming_available,
          ua: navigator.userAgent.slice(0, 200),
        };
        console.warn('[init:stt]', sttDiag);
        try {
          fetch('/api/stt-diag', {
            method: 'POST', body: JSON.stringify(sttDiag),
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
          }).catch(() => {});
        } catch { /* ignore */ }
        // If the server sends a persisted voice_config, seed client prefs from it.
        // This prevents the reconnect race where localStorage (possibly stale) would
        // overwrite the server's in-memory state with the next voice_config send.
        if (msg.voice_config && typeof msg.voice_config === 'object') {
          const vc = msg.voice_config as ServerVoiceConfig;
          onServerVoiceConfigRef.current?.(vc);
        }
        // Seed reasoning toggle from server truth (default true when absent — new identity).
        const initShowReasoning = typeof msg.show_reasoning === 'boolean' ? msg.show_reasoning : true;
        setShowReasoning(initShowReasoning);
        // Seed read-reasoning-aloud from server truth (default false — skip reasoning in TTS).
        const initReadAloud = typeof msg.read_reasoning_aloud === 'boolean' ? msg.read_reasoning_aloud : false;
        setReadReasoningAloud(initReadAloud);
        onServerReasoningConfigRef.current?.(initShowReasoning, initReadAloud);
        break;
      }

      case 'replay_user_message': {
        // Server replays historical user messages on reconnect so the transcript
        // appears complete. Append them to messages state as plain user entries.
        const id = msg.message_id as string;
        const replayContent = msg.content as string;
        // ADR 2026-06-12 (photo-vanish fix): build parts from both text content
        // AND attachments. Server sends attachments on every replay_user_message
        // (server.ts ~:1790 already includes them in the wire). The old handler
        // discarded attachments and built text-only parts, so photo messages showed
        // only their text annotation on every reopen. Fix mirrors the live sendMessage path.
        const replayParts: Array<Record<string, unknown>> = [{ type: 'text', text: replayContent }];
        const rawAttachments = Array.isArray(msg.attachments)
          ? (msg.attachments as Array<{ filename: string; mime_type: string; data: string }>)
          : [];
        for (const att of rawAttachments) {
          if (att.mime_type && att.data) {
            replayParts.push({ type: 'file', mediaType: att.mime_type, filename: att.filename ?? '', url: att.data });
          }
        }
        // Use the server-sent timestamp so reconnect does not reset message times.
        const createdAt = msg.timestamp ? new Date(msg.timestamp as string) : new Date();
        const userMsg = { id, role: 'user', parts: replayParts, createdAt } as unknown as UIMessage;

        if (isReplayingRef.current) {
          // During replay: buffer instead of painting — avoids O(n²) render flood.
          const buf = replayBufferRef.current;
          if (!buf.some((m) => m.id === id)) {
            buf.push(userMsg);
          }
        } else {
          // Live path (should not normally occur, but guard for the edge case where
          // a replay_user_message arrives after settle, e.g. a very slow final frame).
          setMessages((prev) => {
            if (prev.some((m) => m.id === id)) return prev;
            return [...prev, userMsg];
          });
        }
        // Advance watermark so next reconnect skips this message.
        const replayUserSeq = msg.seq as number | undefined;
        if (replayUserSeq !== undefined && replayUserSeq > maxSeqRef.current) {
          maxSeqRef.current = replayUserSeq;
        }
        // Count this replay frame toward the settle counter.
        tickReplay();
        break;
      }

      case 'response_start': {
        const id = msg.message_id as string;
        // A reply to a waiting message means it has been read, whichever frame said so first.
        if (typeof msg.in_reply_to === 'string') {
          const answered = msg.in_reply_to;
          setQueuedMessages((prev) => {
            if (!prev.has(answered)) return prev;
            const next = new Map(prev);
            next.delete(answered);
            return next;
          });
        }
        // If an in-flight message exists (kept alive during tool_use continuation),
        // continue appending to it so the whole multi-turn tool interaction renders
        // as a single assistant message.
        if (!inFlightRef.current) {
          const createdAt = msg.timestamp ? new Date(msg.timestamp as string) : new Date();
          inFlightRef.current = {
            id,
            parts: [],
            textIndex: null,
            thinkingIndex: null,
            toolIndex: new Map(),
            createdAt,
            reasoningRaw: '',
          };
        }
        // Common Room provenance (slice 1): stamp regardless of replay —
        // unlike streaming status below, this must survive reload.
        if (typeof msg.origin === 'string') {
          const origin = msg.origin;
          setMessageOrigin((prev) => {
            const next = new Map(prev);
            next.set(id, origin);
            return next;
          });
        }
        // Session-bound wakes (slice 1): only stamp when the server sent the
        // key at all — hasOwnProperty distinguishes "no origin_session_id on
        // this row" (absent, no chip) from "origin_session_id set but the
        // session has no label / is gone" (present, value null).
        if (Object.prototype.hasOwnProperty.call(msg, 'origin_session_label')) {
          const label = (msg as { origin_session_label?: string | null }).origin_session_label ?? null;
          setMessageOriginSessionLabel((prev) => {
            const next = new Map(prev);
            next.set(id, label);
            return next;
          });
        }
        // Don't flip the composer busy during replay — would cause send-button
        // to flicker busy/ready for every past turn the user sees replayed.
        if (!isReplayingRef.current) setStatus('streaming');
        break;
      }

      case 'response_chunk': {
        if (!isReplayingRef.current) setTurnPhase({ phase: 'streaming' });
        updateInFlight((m) => {
          m.reasoningRaw += (msg.content as string);
          const { reasoning, answer, complete } = splitReasoning(m.reasoningRaw);

          if (reasoning !== '' || m.reasoningRaw.includes(REASONING_OPEN)) {
            // Sentinel block is present — manage reasoning + text parts separately.
            // INVARIANT: the reasoning part is always created at a LOWER index than
            // the text part, so the cleanup in response_end can adjust safely.
            if (m.thinkingIndex === null) {
              // Reuse an existing empty native reasoning slot if present (sealed-model coexistence);
              // otherwise create a new one.
              const emptyReasoningIdx = m.parts.findIndex(
                (p) => p.type === 'reasoning' && !(p as { text: string }).text,
              );
              if (emptyReasoningIdx !== -1) {
                m.thinkingIndex = emptyReasoningIdx;
              } else {
                m.parts.push({ type: 'reasoning', text: '', state: 'streaming' });
                m.thinkingIndex = m.parts.length - 1;
              }
            }
            // Update the reasoning part (covers both the new-slot and reused-slot paths).
            const rPart = m.parts[m.thinkingIndex] as { type: 'reasoning'; text: string; state?: string };
            rPart.text = reasoning;
            rPart.state = complete ? 'done' : 'streaming';

            // Upsert the text part unconditionally (even when answer === '') so
            // textIndex is never left null when a sentinel block exists.
            if (m.textIndex === null) {
              m.parts.push({ type: 'text', text: answer });
              m.textIndex = m.parts.length - 1;
            } else {
              const tPart = m.parts[m.textIndex] as { type: 'text'; text: string };
              tPart.text = answer;
            }
          } else {
            // No sentinel at all — normal message path.
            if (m.textIndex === null) {
              m.parts.push({ type: 'text', text: '' });
              m.textIndex = m.parts.length - 1;
            }
            const part = m.parts[m.textIndex] as { type: 'text'; text: string };
            part.text = answer; // answer === raw when no sentinel present
          }
        });
        break;
      }

      case 'thinking_start': {
        if (!inFlightRef.current) {
          const id = (msg.message_id as string) ?? `msg_${crypto.randomUUID()}`;
          const createdAt = msg.timestamp ? new Date(msg.timestamp as string) : new Date();
          inFlightRef.current = {
            id,
            parts: [],
            textIndex: null,
            thinkingIndex: null,
            toolIndex: new Map(),
            createdAt,
            reasoningRaw: '',
          };
          // Don't flip the composer busy during replay — the gateway replays a
          // thinking_start for every past assistant turn that had thinking, and
          // the matching replay response_end's status reset is guarded out (below).
          // Without this guard, status sticks on 'streaming' after replay settles
          // and the send button is frozen as a red Stop button forever.
          if (!isReplayingRef.current) setStatus('streaming');
        }
        if (!isReplayingRef.current) setTurnPhase({ phase: 'thinking' });
        updateInFlight((m) => {
          m.parts.push({ type: 'reasoning', text: '', state: 'streaming' });
          m.thinkingIndex = m.parts.length - 1;
        });
        break;
      }

      case 'thinking_chunk': {
        updateInFlight((m) => {
          if (m.thinkingIndex === null) return;
          const part = m.parts[m.thinkingIndex] as { type: 'reasoning'; text: string };
          part.text += (msg.content as string) ?? '';
        });
        break;
      }

      case 'thinking_end': {
        updateInFlight((m) => {
          if (m.thinkingIndex === null) return;
          const part = m.parts[m.thinkingIndex] as { type: 'reasoning'; text: string; state?: string };
          part.state = 'done';
          m.thinkingIndex = null;
        });
        break;
      }

      case 'tool_use': {
        const toolName = msg.tool_name as string;
        const toolId = msg.tool_id as string;
        if (!isReplayingRef.current) setTurnPhase({ phase: 'tool_use', toolName });
        updateInFlight((m) => {
          m.parts.push({
            type: `tool-${toolName}`,
            toolCallId: toolId,
            toolName,
            state: 'input-available',
            input: msg.input,
            textOffset: (msg as Record<string, unknown>).text_offset as number | undefined,
          });
          m.toolIndex.set(toolId, m.parts.length - 1);
          m.textIndex = null;
          // Reset the text accumulator so the next response_chunk segment
          // starts fresh. Without this reset, splitReasoning(m.reasoningRaw)
          // returns the entire turn's accumulated text as `answer`, and the
          // new text part pushed after tool_use receives the full prior text
          // again — duplicating every segment printed before the tool call.
          // Safety: reasoningRaw is only used for inline sentinel reasoning
          // (<<<REASONING>>>…<<<END_REASONING>>>), which is always emitted
          // within a single text block and is structurally complete before
          // the API emits a tool_use block. Resetting here cannot truncate
          // a mid-flight sentinel. Native thinking uses the thinking_* channel
          // and never touches reasoningRaw, so thinking display is unaffected.
          m.reasoningRaw = '';
        });
        break;
      }

      case 'tool_result': {
        const toolId = msg.tool_id as string;
        updateInFlight((m) => {
          const idx = m.toolIndex.get(toolId);
          if (idx === undefined) return;
          // Replace the part object (not mutate in-place) so that memo'd
          // ToolCardDispatch sees a new reference and re-renders.
          m.parts[idx] = {
            ...(m.parts[idx] as Record<string, unknown>),
            state: msg.success ? 'output-available' : 'output-error',
            output: msg.output,
            ...(!msg.success ? { errorText: String(msg.output ?? 'error') } : {}),
          } as AssistantPart;
        });
        break;
      }

      case 'agent_status': {
        const agentName = msg.agent as string;
        const agentState = msg.state as string;
        const agentTask = (msg.task as string) ?? '';
        if (agentState === 'running') {
          setActiveAgents((prev) => {
            const existing = prev.find((a) => a.name === agentName);
            if (existing) return prev;
            return [...prev, { name: agentName, task: agentTask, startedAt: Date.now() }];
          });
        } else {
          // complete or error — remove from active list
          setActiveAgents((prev) => prev.filter((a) => a.name !== agentName));
        }
        break;
      }

      case 'response_metrics': {
        const metricsId = msg.message_id as string;
        const metrics: MessageMetrics = {
          input_tokens: (msg.input_tokens as number) ?? 0,
          output_tokens: (msg.output_tokens as number) ?? 0,
          duration_ms: (msg.duration_ms as number) ?? 0,
        };
        if (msg.cache_read_input_tokens != null) {
          metrics.cache_read_input_tokens = msg.cache_read_input_tokens as number;
        }
        if (msg.cache_creation_input_tokens != null) {
          metrics.cache_creation_input_tokens = msg.cache_creation_input_tokens as number;
        }
        if (msg.total_cost_usd != null) {
          metrics.total_cost_usd = msg.total_cost_usd as number;
        }
        if (typeof msg.resolved_model === 'string') {
          metrics.resolved_model = msg.resolved_model;
        }
        if (typeof msg.model === 'string') {
          metrics.model = msg.model;
        }
        // Skip per-replayed-turn metric updates — cosmetic only, and each one
        // allocates a new Map + triggers a render on what may be a long transcript.
        if (!isReplayingRef.current) {
          setMessageMetrics((prev) => {
            const next = new Map(prev);
            next.set(metricsId, metrics);
            return next;
          });
        }
        break;
      }

      case 'response_end': {
        // Advance watermark on every response_end (live and replay).
        // For live messages, seq is stamped by persistForSession before broadcast.
        // For replayed messages, seq is the DB row's seq column.
        const responseEndSeq = (msg as Record<string, unknown>).seq as number | undefined;
        if (responseEndSeq !== undefined && responseEndSeq > maxSeqRef.current) {
          maxSeqRef.current = responseEndSeq;
        }
        if (msg.finish_reason === 'tool_use') {
          // Tool call in progress — keep in-flight alive so the upcoming
          // tool_result can update the tool part with output data.
          // The next response_start will continue the same message.
        } else {
          // Cleanup: if a sentinel reasoning block opened but never closed, promote
          // the inner text to the answer text part and remove the dangling reasoning part.
          const inFlightAtEnd = inFlightRef.current;
          if (inFlightAtEnd && inFlightAtEnd.thinkingIndex !== null) {
            const { complete } = splitReasoning(inFlightAtEnd.reasoningRaw);
            if (!complete && inFlightAtEnd.reasoningRaw.includes(REASONING_OPEN)) {
              // Strip both sentinel markers and promote cleaned text to the text part.
              const cleaned = inFlightAtEnd.reasoningRaw
                .replace(REASONING_OPEN, '')
                .replace(REASONING_CLOSE, '')
                .trim();
              const tIdx = inFlightAtEnd.textIndex;
              if (tIdx !== null) {
                (inFlightAtEnd.parts[tIdx] as { type: 'text'; text: string }).text = cleaned;
              }
              // Remove the dangling reasoning part.
              // INVARIANT: reasoning part is at a lower index than the text part
              // (enforced during chunk processing above), so splicing it does not
              // shift the text part out of view — only decrement textIndex by 1.
              const rIdx = inFlightAtEnd.thinkingIndex;
              inFlightAtEnd.parts.splice(rIdx, 1);
              if (inFlightAtEnd.textIndex !== null && inFlightAtEnd.textIndex > rIdx) {
                inFlightAtEnd.textIndex -= 1;
              }
              inFlightAtEnd.thinkingIndex = null;
            }
          }
          commitInFlight();
          // Don't flip the composer ready during replay — would cause send-button
          // to flicker busy/ready for every past turn the user sees replayed.
          // settleReplay() leaves status as-is; it was 'ready' before replay armed.
          // A response_end ends a SEGMENT, not the turn (tool rounds, an interjection
          // split, a queued message sent right behind it): the server's turn_state
          // {in_flight:false} returns the composer to ready. An error ends the turn here.
          if (!isReplayingRef.current && msg.finish_reason === 'error') {
            setStatus('ready');
            setTurnPhase(null);
            turnStartedAtRef.current = null;
          }
          // A real answer ends a flag streak. Errored and replayed ends do not.
          if (msg.finish_reason !== 'error' && msg.finish_reason !== 'replay') {
            setSafeguardState(null);
          }
        }
        // Count replay assistant turns toward the settle counter.
        // finish_reason 'replay' discriminates replay frames from live ones so a
        // live message arriving mid-window doesn't corrupt the counter.
        if (msg.finish_reason === 'replay') {
          tickReplay();
        }
        break;
      }

      case 'error': {
        console.error('[sovereign-muse] error:', msg);
        const errorText = String(msg.message ?? 'The model returned no response.');
        // If an assistant turn was in-flight when the error arrived, finalize it
        // so the UI does not freeze with an open thinking block and a stuck composer.
        // Pattern mirrors the response_end cleanup (lines above): close any open
        // reasoning part, append a visible error part, then commit and reset status.
        const inFlightOnError = inFlightRef.current;
        if (inFlightOnError) {
          // Close any open reasoning block (dangling thinkingIndex).
          if (inFlightOnError.thinkingIndex !== null) {
            const rPart = inFlightOnError.parts[inFlightOnError.thinkingIndex] as {
              type: 'reasoning';
              text: string;
              state?: string;
            };
            rPart.state = 'done';
            inFlightOnError.thinkingIndex = null;
          }
          // Append the error text. If a text part is already open (partial answer
          // streamed before the error), append in-place to avoid creating a second
          // text part that renderers keyed on textIndex would silently drop.
          // Only when textIndex is null (no partial answer yet) do we push a new part.
          if (inFlightOnError.textIndex !== null) {
            const tPart = inFlightOnError.parts[inFlightOnError.textIndex] as {
              type: 'text';
              text: string;
            };
            tPart.text += `\n\n[Error: ${errorText}]`;
          } else {
            inFlightOnError.parts.push({ type: 'text', text: `\n\n[Error: ${errorText}]` });
          }
          commitInFlight();
        }
        // Always reset status to ready so the composer is usable again.
        // For recoverable errors the client can retry; for non-recoverable ones
        // the error state is shown via setError but the send button stays live.
        setStatus('ready');
        if (msg.code === 'safeguard_flagged') {
          const flaggedSession = activeSessionIdRef.current;
          setSafeguardState((prev) => ({
            sessionId: flaggedSession,
            flag: {
              message: errorText,
              count: prev && prev.sessionId === flaggedSession ? prev.flag.count + 1 : 1,
            },
          }));
        }
        if (!msg.recoverable) {
          setError(new Error(errorText));
        }
        break;
      }

      case 'provider_status': {
        const state = msg.state as string;
        if (state === 'switching') {
          switchingRef.current = true;
          setSwitching(true);
          // Safety timeout: if server never sends ready/error (async crash after
          // restart() returns), reset switching after 15s so UI doesn't freeze.
          if (switchingTimeoutRef.current) clearTimeout(switchingTimeoutRef.current);
          switchingTimeoutRef.current = window.setTimeout(() => {
            if (switchingRef.current) {
              switchingRef.current = false;
              setSwitching(false);
            }
          }, 15_000);
        } else if (state === 'ready') {
          switchingRef.current = false;
          setSwitching(false);
          if (switchingTimeoutRef.current) {
            clearTimeout(switchingTimeoutRef.current);
            switchingTimeoutRef.current = null;
          }
          // RC2 fix: sync the client's displayed model to the ACTUALLY-running model.
          // The gateway sends msg.model in every provider_status frame (required field
          // on ProviderStatusMessage). Without this, the chat bar model display
          // freezes at whatever config.model was pre-switch — so picking Opus 4.8
          // shows "switching…" then reverts to whatever was in the picker rather than
          // confirming the switch succeeded.
          if (typeof msg.model === 'string' && msg.model.length > 0) {
            setServerModel(msg.model as string);
          }
        } else if (state === 'error') {
          switchingRef.current = false;
          setSwitching(false);
          if (switchingTimeoutRef.current) {
            clearTimeout(switchingTimeoutRef.current);
            switchingTimeoutRef.current = null;
          }
          setError(new Error(String(msg.message ?? 'model switch failed')));
        }
        break;
      }

      case 'tts_start':
        onTTSStartRef.current?.(
          msg.messageId as string,
          msg.sampleRate as number,
          msg.voiceId as string,
          typeof msg.sourceId === 'string' ? msg.sourceId : undefined,
        );
        break;

      case 'tts_end':
        onTTSEndRef.current?.(msg.messageId as string, typeof msg.sourceId === 'string' ? msg.sourceId : undefined);
        break;

      case 'tts_notice':
        // Non-destructive: log the TTS issue without touching the in-flight
        // model response. The text stream may still be alive — commitInFlight()
        // here would kill it (the bug this message type exists to prevent).
        console.warn('[tts] notice:', msg.message);
        // Reeve HIGH, PR #68 2nd round: only the two server-side emission
        // sites this actually concerns (handleManualTTSRequest's onError,
        // the auto-stream path's onError) tag the frame with a messageId —
        // an untagged/legacy notice shape still logs above but has no id
        // to attribute a failure to, so it correctly does nothing further.
        if (typeof msg.messageId === 'string') {
          onTTSErrorRef.current?.(msg.messageId, typeof msg.sourceId === 'string' ? msg.sourceId : undefined);
        }
        break;

      case 'stt_result':
        console.log('[stt-recv] stt_result:', (msg.text as string)?.slice(0, 80));
        onSTTResultRef.current?.(msg.text as string);
        break;

      case 'stt_error':
        console.error('[stt-recv] stt_error:', msg.message);
        onSTTResultRef.current?.('');
        break;

      case 'stt_stream_ready':
        onSTTStreamReadyRef.current?.();
        break;

      case 'stt_stream_done':
        onSTTStreamDoneRef.current?.();
        break;

      case 'stt_stream_segment':
        onSTTStreamSegmentRef.current?.(msg.text as string, msg.completed as boolean);
        break;

      case 'stt_stream_error':
        console.error('[sovereign-muse] streaming STT error:', msg.message);
        onSTTStreamErrorRef.current?.(msg.message as string, msg.fallback as boolean);
        break;

      case 'reaction': {
        const rMsg = {
          messageId: msg.message_id as string,
          emoji: msg.emoji as string,
          user: msg.user as string,
          action: msg.action as 'add' | 'remove',
        };
        console.log('[useChat] reaction:', rMsg.messageId, rMsg.emoji, rMsg.user, rMsg.action, 'ref wired:', !!onReactionRef.current);
        if (onReactionRef.current) {
          onReactionRef.current(rMsg.messageId, rMsg.emoji, rMsg.user, rMsg.action);
        } else {
          pendingReactionsRef.current.push(rMsg);
        }
        break;
      }

      case 'reaction_sync': {
        const reactions = Array.isArray(msg.reactions) ? msg.reactions as Array<{ message_id: string; emoji: string; user: string }> : [];
        console.log('[useChat] reaction_sync:', reactions.length, 'reactions, ref wired:', !!onReactionSyncRef.current);
        if (onReactionSyncRef.current) {
          onReactionSyncRef.current(reactions);
        } else {
          pendingReactionSyncsRef.current.push(reactions);
        }
        break;
      }

      case 'writing_room_document_updated': {
        if (typeof msg.project_id === 'string' && typeof msg.version === 'string') {
          onWritingRoomDocumentUpdatedRef.current?.({
            project_id: msg.project_id,
            version: msg.version,
            ...(typeof msg.actor === 'string' ? { actor: msg.actor } : {}),
          });
        }
        break;
      }

      case 'queued': {
        // A message waiting to be read. Also covers one typed on another device,
        // which has no local bubble yet.
        const id = msg.message_id as string;
        const takeBack = msg.take_back === true;
        setQueuedMessages((prev) => {
          const next = new Map(prev);
          next.set(id, { takeBack });
          return next;
        });
        addUserBubbleIfMissing(id, String(msg.content ?? ''), msg.created_at as string | undefined);
        break;
      }

      case 'queue_state': {
        // The server's whole waiting list (on connect, or after a refused take-back).
        const items = Array.isArray(msg.items)
          ? (msg.items as Array<{ message_id: string; content?: string; take_back?: boolean; created_at?: string }>)
          : [];
        setQueuedMessages(new Map(items.map((it) => [it.message_id, { takeBack: it.take_back === true }])));
        for (const it of items) addUserBubbleIfMissing(it.message_id, String(it.content ?? ''), it.created_at);
        break;
      }

      case 'queue_taken': {
        // The companion read it. Where depends on the lane: a Claude reply read it mid-way
        // (after_message_id = the segment that closed there), otherwise it follows the reply.
        const id = msg.message_id as string;
        const after = typeof msg.after_message_id === 'string' ? msg.after_message_id : null;
        const takenSeq = msg.seq as number | undefined;
        if (takenSeq !== undefined && takenSeq > maxSeqRef.current) maxSeqRef.current = takenSeq;
        setQueuedMessages((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Map(prev);
          next.delete(id);
          return next;
        });
        // Close the open reply segment so the bubble sits between it and the next one.
        if (after !== null) commitInFlight();
        setMessages((prev) => {
          const from = prev.findIndex((m) => m.id === id);
          if (from < 0) return prev;
          const without = [...prev.slice(0, from), ...prev.slice(from + 1)];
          const anchor = after === null ? -1 : without.findIndex((m) => m.id === after);
          const at = anchor < 0 ? without.length : anchor + 1;
          return [...without.slice(0, at), prev[from]!, ...without.slice(at)];
        });
        break;
      }

      case 'queue_cancelled': {
        const id = msg.message_id as string;
        setQueuedMessages((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Map(prev);
          next.delete(id);
          return next;
        });
        setMessages((prev) => prev.filter((m) => m.id !== id));
        setTakeBackDraft({ key: id, text: String(msg.content ?? '') });
        break;
      }

      case 'turn_state': {
        // Sent as the LAST frame of the replay sequence on every (re)connect.
        // Also sent live when a turn is claimed (true) and closed (false); the composer's
        // busy state follows it, not each response_end.
        // in_flight false resets the status to ready; true makes a ready composer busy.
        // Use functional setter to read current status inside a stable useCallback
        // without adding `status` to the dependency array (which would cause
        // handleServerMessage to re-create on every status change — a regression risk).
        if (msg.in_flight === true) {
          // A turn is open server-side (claimed, between segments, or after a reconnect):
          // keep the composer busy even though no response_start has arrived yet.
          setStatus((prev) => prev === 'ready' ? 'submitted' : prev);
        }
        if (msg.in_flight === false) {
          commitInFlight();
          setStatus((prev) => (prev === 'submitted' || prev === 'streaming') ? 'ready' : prev);
          setTurnPhase(null);
          turnStartedAtRef.current = null;
        }
        break;
      }

      case 'heartbeat':
        if (typeof msg.subprocess_alive === 'boolean') {
          setSubprocessAlive(msg.subprocess_alive as boolean);
        }
        break;
      case 'pong':
        break;

      default:
        console.debug('[sovereign-muse] unhandled:', type);
    }
  }, [commitInFlight, updateInFlight, settleReplay, armReplayDebounce, tickReplay, addUserBubbleIfMissing]);

  const connect = useCallback((targetSessionId?: string | null) => {
    const token = getStoredToken();
    if (!token) return;

    const identity = identityRef.current;
    if (!identity) return;

    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    // PATH B SLICE 4: identity comes from the active session's companion
    // (identityRef, read fresh here rather than closed-over) so /ws/${identity}
    // connects to the right companion — including across a session switch that
    // crosses companions, since the sessionId-keyed effect below re-invokes
    // connect() and identityRef is already updated by then (set in render body).
    const base = `${proto}://${location.host}/ws/${identity}`;
    let url = targetSessionId ? `${base}?session=${encodeURIComponent(targetSessionId)}` : base;
    // ADR 2026-06-12 (holding handshake): declare actual in-memory state so the server
    // can make the full-vs-gap replay decision authoritatively.
    // holding=1 iff the client has messages right now (genuine same-page reconnect).
    // holding=0 on every cold mount, refresh, bfcache-miss, or new tab.
    // messagesRef is a stable ref (synced via effect) so it reads current state even
    // inside a useCallback closure without needing messages in the dep array.
    const holding = messagesRef.current.length > 0 ? 1 : 0;
    url += `${url.includes('?') ? '&' : '?'}holding=${holding}`;
    // Only send last_seen_seq when holding=1 (dedup hint for gap-replay).
    // Omit on cold mounts — server ignores it when holding=0, and omitting makes
    // the invariant explicit: a fresh page never asserts a seq position.
    // NOTE: holding=1 with maxSeqRef.current=0 means messages are in memory but no
    // seq watermark was recorded. Server receives holding=1 and no last_seen_seq →
    // resolveReplaySince returns 0 → full replay. Dedup in settleReplay() handles
    // any double-delivery safely.
    if (holding === 1 && maxSeqRef.current > 0) {
      url += `&last_seen_seq=${maxSeqRef.current}`;
    }

    let ws: WebSocket;
    try {
      ws = new WebSocket(url, [`bearer.${token}`]);
    } catch {
      setError(new Error('auth_failed'));
      return;
    }
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.addEventListener('open', () => {
      setConnected(true);
      reconnectDelayRef.current = 1000;
      hasConnectedRef.current = true;
      connectedSessionRef.current = targetSessionId;
      consecutiveWsFailuresRef.current = 0;
      // Send current visibility state once on open so the gateway knows whether
      // this reconnecting client is hidden — avoids TTS-into-void on reconnects
      // from a backgrounded tab.
      const isVisible = document.visibilityState === 'visible';
      ws.send(JSON.stringify({ type: 'visibility', visible: isVisible }));
    });

    ws.addEventListener('close', (ev) => {
      if (wsRef.current !== ws) return;
      setConnected(false);
      // A pending model switch can't be confirmed without a live connection.
      // Clear it so the dialog doesn't persist across disconnects.
      setPendingModelSwitch(null);
      // Reset STT availability — will be re-set from the next init message on reconnect.
      // Without this, the streaming STT tier stays selected during WS-down windows,
      // sendStreamStart silently fails, and the mic button gets stuck yellow.
      console.warn('[ws:close] code:', ev.code, '— resetting STT state');
      // Streaming STT state is connection-scoped on the gateway. A recorder
      // must never follow wsRef onto the replacement socket without a fresh
      // stt_stream_start handshake.
      onSTTStreamDisconnectRef.current?.();
      setSttAvailable(false);
      setSttStreamingAvailable(false);
      wsRef.current = null;
      if (ev.code === 1008 || ev.code === 4401) {
        setError(new Error('auth_failed'));
        return;
      }
      consecutiveWsFailuresRef.current += 1;

      // After several rapid WS failures without a successful open, verify
      // the token via REST before assuming auth failure. Browser WS close
      // code 1006 is ambiguous — it could be auth rejection at the HTTP
      // upgrade level, a server crash, or a network error. A REST check
      // resolves the ambiguity without nuking a valid token.
      if (consecutiveWsFailuresRef.current >= 3) {
        consecutiveWsFailuresRef.current = 0;
        const checkToken = getStoredToken();
        if (checkToken) {
          fetch('/api/sessions', {
            headers: { 'Authorization': `Bearer ${checkToken}` },
          }).then(res => {
            if (res.status === 401) {
              setError(new Error('auth_failed'));
            }
          }).catch(() => {
            // Network error — server unreachable, reconnect will handle it
          });
        }
      }

      if (reconnectTimerRef.current !== null) return;
      const sessionToReconnect = connectedSessionRef.current;
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        connect(sessionToReconnect);
      }, reconnectDelayRef.current);
      reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 1.5, 15_000);
    });

    ws.addEventListener('error', (err) => {
      console.error('[sovereign-muse] ws error:', err);
      setConnected(false);
    });

    ws.addEventListener('message', (ev) => {
      // Binary frame = raw Float32Array audio chunk from server TTS
      if (ev.data instanceof ArrayBuffer) {
        onServerAudioChunkRef.current?.(ev.data);
        return;
      }
      let msg: Record<string, unknown>;
      try {
        msg = JSON.parse(ev.data as string);
      } catch {
        return;
      }
      handleServerMessage(msg);
    });
  }, [handleServerMessage]);

  // MUSE GUIDE: SEND A CHAT MESSAGE — writes the user turn to the WS connection.
  const sendMessage = useCallback(
    (args: SendMessageArgs) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        setError(new Error('not connected'));
        return;
      }
      if (switchingRef.current) return;

      // §0.3c: block a too-long send client-side rather than round-tripping
      // to the gateway just to be rejected. Chunking is wrong (it would
      // silently split one turn into several) and building a new upload
      // path is out of scope — an attach-as-file path already exists
      // (ChatInput's "attach files" button accepts text/markdown), so the
      // fix is to point at it, not invent a second one.
      if (args.text.length > MAX_USER_MESSAGE_CHARS) {
        setError(new Error(
          `Message is ${args.text.length.toLocaleString('en-US')} characters — the limit is ` +
          `${MAX_USER_MESSAGE_CHARS.toLocaleString('en-US')}. Attach it as a file instead (the paperclip button).`,
        ));
        return;
      }

      const busy = statusRef.current === 'submitted' || statusRef.current === 'streaming';
      const userMsgId = `msg_${crypto.randomUUID()}`;
      const parts: Array<Record<string, unknown>> = [{ type: 'text', text: args.text }];
      if (args.files?.length) {
        for (const f of args.files) {
          parts.push({
            type: 'file',
            mediaType: f.mediaType,
            filename: f.filename,
            url: f.url,
          });
        }
      }
      setMessages((prev) => [
        ...prev,
        { id: userMsgId, role: 'user', parts: parts as UIMessage['parts'], createdAt: new Date() } as UIMessage,
      ]);

      // WR-2: read the getter ONCE, at send time — never on the typing path.
      // undefined (not called, or the getter itself returns null) omits the
      // key entirely from the JSON.stringify output, keeping the frame
      // byte-identical to pre-WR-2 for every non-writing-room send.
      const room_state = roomStateRef.current?.() ?? undefined;

      ws.send(
        JSON.stringify({
          type: 'message',
          id: userMsgId,
          content: args.text,
          timestamp: new Date().toISOString(),
          attachments: args.files?.map((f) => ({
            filename: f.filename,
            mime_type: f.mediaType,
            data: f.url,
          })),
          room_state,
        }),
      );
      if (busy) {
        // An interjection: the reply keeps running, so its status and clock stay as they are.
        // Dimmed until the server says otherwise (queue_taken, or the reply it starts).
        setQueuedMessages((prev) => new Map(prev).set(userMsgId, { takeBack: false }));
        return;
      }
      setStatus('submitted');
      setTurnPhase({ phase: 'submitted' });
      turnStartedAtRef.current = Date.now();
    },
    [],
  );

  const cancelQueued = useCallback((messageId: string) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'queue_cancel', message_id: messageId }));
  }, []);

  const stop = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'interrupt', id: `int_${crypto.randomUUID()}` }));
  }, []);

  const clearHistory = useCallback(() => {
    setMessages([]);
    // Keep the ref invariant: every messages wipe zeroes messagesRef synchronously
    // (see session-switch) so a connect() on a later tick can't read stale frames
    // and declare holding=1 on an empty view.
    messagesRef.current = [];
    setMessageMetrics(new Map());
    setMessageOrigin(new Map());
    setMessageOriginSessionLabel(new Map());
    setQueuedMessages(new Map());
    setTakeBackDraft(null);
    inFlightRef.current = null;
    replayBufferRef.current = [];
    setTurnPhase(null);
    turnStartedAtRef.current = null;
  }, []);

  const sendTTSRequest = useCallback((messageId: string, text: string, voiceId: string, requestId: string) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'tts_request', messageId, text, voiceId, requestId }));
  }, []);

  const sendTTSStop = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'tts_stop' }));
  }, []);

  const sendVoiceConfig = useCallback((autoSpeak: boolean, ttsMode: string, voiceId: string, speed: number) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'voice_config', autoSpeak, ttsMode, voiceId, speed }));
  }, []);

  const sendReasoningConfig = useCallback((enabled: boolean, readAloud?: boolean) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const payload: Record<string, unknown> = { type: 'reasoning_config', enabled };
    if (typeof readAloud === 'boolean') {
      payload.readReasoningAloud = readAloud;
    }
    ws.send(JSON.stringify(payload));
    // Flip local state only after the send succeeds — the toggle reflects the
    // server's actual knowledge. If the socket is closed we don't flip; the
    // server re-hydrates the real value on reconnect.
    setShowReasoning(enabled);
    if (typeof readAloud === 'boolean') {
      setReadReasoningAloud(readAloud);
    }
  }, []);

  const sendRawWs = useCallback((payload: Record<string, unknown>) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify(payload));
  }, []);

  const sendSTTStreamStart = useCallback((language?: string) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'stt_stream_start', language }));
  }, []);

  const sendSTTStreamStop = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'stt_stream_stop' }));
  }, []);

  const sendBinaryFrame = useCallback((data: ArrayBuffer) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(data);
  }, []);

  const sendSTTAudio = useCallback((blob: Blob, format = 'webm') => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn('[stt-send] WS not open, blob dropped', ws?.readyState);
      return;
    }
    console.log('[stt-send] reading blob', blob.size, 'bytes, format:', format);
    // Two-frame protocol: read blob first, then send header + binary atomically.
    // Both sends happen inside the .then() so they land in the same WS frame queue
    // with no gap for a binary-without-header race.
    blob.arrayBuffer().then((buf) => {
      if (ws.readyState !== WebSocket.OPEN) {
        console.warn('[stt-send] WS closed before send, buf dropped', ws.readyState);
        return;
      }
      console.log('[stt-send] sending JSON header + binary', buf.byteLength, 'bytes');
      ws.send(JSON.stringify({ type: 'stt_audio', format }));
      ws.send(buf);
      console.log('[stt-send] both frames sent OK');
    });
  }, []);

  useEffect(() => {
    // Session not yet resolved (still loading from REST). Skip — connecting with
    // a null session id would open a duplicate connection that the server routes
    // to its own fallback session, causing a double-replay when the real session
    // id arrives and the effect re-runs with the correct id.
    if (sessionId === null || !connectionIdentity) {
      // Explicit id + missing server-authoritative identity is an incoherent
      // target, not permission to guess. The prior effect cleanup closes any
      // old socket; clear residual reconnect state so visibility/timers cannot
      // resurrect it under another companion.
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      connectedSessionRef.current = undefined;
      setConnected(false);
      return;
    }

    // If the session ID changed, close the current connection immediately
    // so we reconnect to the correct session.
    if (connectedSessionRef.current !== undefined && connectedSessionRef.current !== sessionId) {
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (replayDebounceRef.current !== null) {
        clearTimeout(replayDebounceRef.current);
        replayDebounceRef.current = null;
      }
      wsRef.current?.close();
      wsRef.current = null;
      // Clear messages when switching sessions.
      // messagesRef.current is zeroed SYNCHRONOUSLY here so that the immediately
      // following connect() call reads holding=0 — the ref-effect sync runs
      // post-commit and would otherwise leave stale frames visible for one tick,
      // causing holding=1 on a cold session-switch.
      setMessages([]);
      setQueuedMessages(new Map());
      setTakeBackDraft(null);
      messagesRef.current = [];
      inFlightRef.current = null;
      replayBufferRef.current = [];
      setTurnPhase(null);
      turnStartedAtRef.current = null;
      // Reset watermark — new session has its own seq space.
      maxSeqRef.current = 0;
    }
    connect(sessionId);
    return () => {
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (switchingTimeoutRef.current !== null) {
        clearTimeout(switchingTimeoutRef.current);
        switchingTimeoutRef.current = null;
      }
      if (replayDebounceRef.current !== null) {
        clearTimeout(replayDebounceRef.current);
        replayDebounceRef.current = null;
      }
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [connect, connectionIdentity, sessionId]);

  // Reconnect immediately when the app returns from background (iOS kills WS while backgrounded)
  useEffect(() => {
    function handleVisibility() {
      if (document.visibilityState !== 'visible') return;
      const ws = wsRef.current;
      if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
        if (reconnectTimerRef.current !== null) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
        reconnectDelayRef.current = 1000;
        connect(connectedSessionRef.current);
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [connect]);

  // Visibility beacon — informs the gateway whether this client is visible so
  // it can skip TTS synthesis when no client is watching. Debounced 250ms to
  // avoid flooding on rapid tab switches. Transitions only (not polled).
  // The initial beacon on WS open is handled in the ws.addEventListener('open')
  // block inside connect() above.
  useEffect(() => {
    function sendBeacon(visible: boolean) {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ type: 'visibility', visible }));
    }

    function handleVisibilityBeacon() {
      if (visibilityBeaconTimerRef.current !== null) {
        clearTimeout(visibilityBeaconTimerRef.current);
        visibilityBeaconTimerRef.current = null;
      }
      // Read visibility INSIDE the timer callback so rapid tab-switches collapse
      // to one beacon carrying the FINAL state at timer-fire time, not the state
      // captured when the timer was scheduled (which may be stale after another flip).
      visibilityBeaconTimerRef.current = window.setTimeout(() => {
        visibilityBeaconTimerRef.current = null;
        sendBeacon(document.visibilityState === 'visible');
      }, 250);
    }

    document.addEventListener('visibilitychange', handleVisibilityBeacon);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityBeacon);
      if (visibilityBeaconTimerRef.current !== null) {
        clearTimeout(visibilityBeaconTimerRef.current);
        visibilityBeaconTimerRef.current = null;
      }
    };
  }, []);

  // A model-switch confirmation is session-owned intent. Cancel and revert it
  // before any replacement socket can observe it during a route transition.
  const modelSwitchSessionRef = useRef(sessionId);
  useEffect(() => {
    if (modelSwitchSessionRef.current === sessionId) return;
    const pending = pendingModelSwitchRef.current;
    if (pending) {
      onRevertChatModelRef.current?.(pending.from);
      // Keep the ref authoritative within this effect flush; the state render
      // may not land before the config-change effect below runs.
      pendingModelSwitchRef.current = null;
      setPendingModelSwitch(null);
    }
    modelSwitchSessionRef.current = sessionId;
  }, [sessionId]);

  // Send config_change to gateway when model or effort changes.
  // Option A fix: onSyncChatModel in the init handler updates chatConfig.model
  // in the same React batch as setServerModel, then resets lastSentConfigRef to
  // null so this self-seed fires with the correct (server) model already in place.
  //
  // `connected` is in the deps so the effect fires on every new connection —
  // guaranteeing the self-seed happens even when the server model equals the
  // client's current chatConfig.model (i.e. deps wouldn't otherwise change).
  useEffect(() => {
    if (!chatConfig) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      // Thorn diagnosis: socket isn't OPEN (CONNECTING mid-reconnect, or CLOSING/
      // CLOSED) at the moment chatConfig.model changed. Don't silently drop the
      // intent — queue it. Resolved once the next init frame's onSyncChatModel
      // settles (see the `!last` self-seed branch below).
      //
      // offlineConfigBaselineRef distinguishes an actual offline model change from
      // this effect's own first mount-time firing (which has no prior baseline and
      // must not queue a phantom switch against whatever the server later syncs).
      const baseline = offlineConfigBaselineRef.current;
      if (baseline && baseline.model !== chatConfig.model) {
        queuedModelSwitchRef.current = {
          model: chatConfig.model,
          effort: chatConfig.effort,
          requestedAt: Date.now(),
        };
        console.warn('[model-switch] intent queued — socket not OPEN', {
          readyState: ws ? ws.readyState : null,
          requested: chatConfig.model,
        });
      }
      offlineConfigBaselineRef.current = { model: chatConfig.model, effort: chatConfig.effort };
      return;
    }
    // Back to OPEN processing: the offline baseline no longer applies — a future
    // disconnect should start tracking fresh from whatever's current then.
    offlineConfigBaselineRef.current = null;

    const last = lastSentConfigRef.current;

    // First run after connect: self-seed from the now-synced chatConfig.
    // onSyncChatModel ensures chatConfig.model equals the server model by the time
    // this runs, so the seed is always authoritative — no stale-default race.
    if (!last) {
      lastSentConfigRef.current = { model: chatConfig.model, effort: chatConfig.effort };

      // Resolve a queued offline model-switch intent now that the sync has settled.
      const queued = queuedModelSwitchRef.current;
      if (queued) {
        queuedModelSwitchRef.current = null;
        if (queued.model !== chatConfig.model) {
          // The queued pick never reached the gateway before the reconnect — re-trigger
          // the normal switch flow for it, exactly as if the user had tapped it with a
          // live socket (pendingModelSwitch → confirm/cancel dialog, same as the OPEN path).
          const originSessionId = connectedSessionRef.current;
          if (typeof originSessionId === 'string' && originSessionId === sessionId) {
            console.warn('[model-switch] resolving queued intent — synced model differs', {
              queued: queued.model,
              synced: chatConfig.model,
            });
            setPendingModelSwitch({
              from: chatConfig.model,
              to: queued.model,
              effort: queued.effort,
              session_id: originSessionId,
            });
          }
        }
        // else: queued pick matches the synced model — server already has it, nothing to do.
      }
      return;
    }

    if (last.model === chatConfig.model && last.effort === chatConfig.effort) {
      // A1 fix: if the user reverted the picker to the last-sent model while a dialog
      // was open (e.g. A→B dialog open → user flips back to A), the pending is now
      // stale — the dialog's Confirm would send B against an already-reverted picker.
      // Clear it so the dialog closes without firing a spurious config_change.
      if (pendingModelSwitchRef.current) setPendingModelSwitch(null);
      return;
    }

    // MODEL changed: gate with confirmation dialog before restarting the subprocess.
    // Effort-only changes restart the subprocess without changing the model — pass
    // through immediately (no dialog needed, lower stakes).
    if (last.model !== chatConfig.model) {
      const originSessionId = connectedSessionRef.current;
      if (typeof originSessionId !== 'string' || originSessionId !== sessionId) return;
      // A1 fix: skip if the same destination is already pending — the existing dialog
      // is still valid and re-setting would flash it or duplicate the transition.
      if (
        pendingModelSwitchRef.current?.to !== chatConfig.model ||
        pendingModelSwitchRef.current.session_id !== originSessionId
      ) {
        setPendingModelSwitch({
          from: last.model,
          to: chatConfig.model,
          effort: chatConfig.effort,
          session_id: originSessionId,
        });
      }
      return;
    }

    // Effort-only change: send immediately.
    const originSessionId = connectedSessionRef.current;
    if (typeof originSessionId !== 'string' || originSessionId !== sessionId) return;
    lastSentConfigRef.current = { model: chatConfig.model, effort: chatConfig.effort };
    ws.send(JSON.stringify({
      type: 'config_change',
      model: chatConfig.model,
      effort: chatConfig.effort,
      session_id: originSessionId,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatConfig?.model, chatConfig?.effort, connected]);

  const confirmModelSwitch = useCallback(() => {
    const pending = pendingModelSwitchRef.current;
    if (!pending) return;
    const ws = wsRef.current;
    const stillOnOrigin = activeSessionIdRef.current === pending.session_id &&
      connectedSessionRef.current === pending.session_id;
    if (!stillOnOrigin || !ws || ws.readyState !== WebSocket.OPEN) {
      // A2 fix: socket is CONNECTING or already CLOSED — can't send config_change.
      // Treat as cancel: revert the picker and clear the dialog rather than leaving
      // it open with a dead Confirm button.
      onRevertChatModelRef.current?.(pending.from);
      setPendingModelSwitch(null);
      return;
    }
    lastSentConfigRef.current = { model: pending.to, effort: pending.effort };
    ws.send(JSON.stringify({
      type: 'config_change',
      model: pending.to,
      effort: pending.effort,
      session_id: pending.session_id,
    }));
    setPendingModelSwitch(null);
  }, []);

  const cancelModelSwitch = useCallback(() => {
    const pending = pendingModelSwitchRef.current;
    if (!pending) return;
    // Revert chatConfig.model to the last-confirmed model so the picker snaps back.
    onRevertChatModelRef.current?.(pending.from);
    setPendingModelSwitch(null);
  }, []);

  const isLoading = status === 'submitted' || status === 'streaming';

  const safeguardFlag = safeguardState && safeguardState.sessionId === sessionId ? safeguardState.flag : null;

  return {
    queuedMessages,
    cancelQueued,
    takeBackDraft,
    safeguardFlag,
    messages,
    messageMetrics,
    messageOrigin,
    messageOriginSessionLabel,
    status,
    isLoading,
    connected,
    switching,
    hasConnected: startupGrace ? false : hasConnectedRef.current,
    error,
    activeAgents,
    serverModel,
    serverModels,
    serverCompanions,
    isReplaying,
    sendMessage,
    stop,
    clearHistory,
    sendTTSRequest,
    sendTTSStop,
    sendVoiceConfig,
    sendReasoningConfig,
    showReasoning,
    readReasoningAloud,
    onServerReasoningConfigRef,
    sendRawWs,
    onWritingRoomDocumentUpdatedRef,
    roomStateRef,
    onServerAudioChunkRef,
    onTTSStartRef,
    onTTSEndRef,
    onTTSErrorRef,
    sttAvailable,
    sttStreamingAvailable,
    sendSTTAudio,
    sendSTTStreamStart,
    sendSTTStreamStop,
    sendBinaryFrame,
    onSTTResultRef,
    onSTTStreamSegmentRef,
    onSTTStreamReadyRef,
    onSTTStreamDoneRef,
    onSTTStreamDisconnectRef,
    onSTTStreamErrorRef,
    onReactionRef,
    onReactionSyncRef,
    pendingReactionsRef,
    pendingReactionSyncsRef,
    turnPhase,
    turnStartedAt: turnStartedAtRef.current,
    subprocessAlive,
    pendingModelSwitch,
    confirmModelSwitch,
    cancelModelSwitch,
  };
}
